import { useState, useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../../../redux/store";
import { useSnackbar } from "notistack";

import {
  Box,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Divider,
  CircularProgress,
  Typography,
  MenuItem,
  Alert,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DeleteIcon from "@mui/icons-material/Delete";

import {
  changeFilter,
  clearFilters,
} from "../../../../redux/slices/vendorsSlice";
import PaperWrapper from "../../../../components/PaperWrapper";
import TableControls from "../../../../components/Table/components/TableControls";
import SyncSettingsModal from "./SyncSettingsModal";
import {
  fetchVendorInsuranceMerges,
  generateXlsxReport,
  initiateFilesMerge as initiateFilesMergeThunk,
  getMissingFilesReport,
  deleteVendorInsuranceMerge,
  setFilter as setMergesFilter,
  setPage as setMergesPage,
  clearGenerateSuccess,
  clearMergeSuccess,
  clearMergeInProgress,
  clearMissingReportSuccess,
  clearDeleteSuccess,
} from "../../../../redux/slices/vendorMergesSlice";

const VendorsTableControls = ({
  selectedVendors = [],
  sendStatus,
  onSendLienWaiverNotices,
  loading = false,
  isFullscreen = false,
  onToggleFullscreen,
  onExport,
  // insuranceReportStatus,
  // onDownloadInsuranceReport,
  // tpFoldersReportStatus,
  // onDownloadTPFoldersReport,
}: any) => {
  const dispatch = useAppDispatch();
  const filters = useAppSelector((state) => state.vendors.filters) as any;
  const { enqueueSnackbar } = useSnackbar();

  const [syncSettingsModalOpen, setSyncSettingsModalOpen] = useState(false);
  const [insuranceDialogOpen, setInsuranceDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<any>(null);

  const merges = useAppSelector((state) => state.vendorMerges || {});
  const mergedFiles = merges.items || [];
  const mergedListStatus = merges.status || "idle";
  const mergedListError = merges.error || null;
  const mergesPage = merges.page || 1;
  const mergesLimit = merges.limit || 10;
  const mergesTotalPages =
    merges.totalPages ||
    (typeof merges.total === "number"
      ? Math.ceil(merges.total / merges.limit)
      : null);
  const mergesFilters = merges.filters || {};
  const generateStatus = merges.generateStatus || "idle";
  const generateError = merges.generateError || null;
  const generateSuccess = merges.generateSuccess || null;
  const mergeStatus = merges.mergeStatus || "idle";
  const mergeError = merges.mergeError || null;
  const mergeSuccess = merges.mergeSuccess || null;
  const mergeInProgress = merges.mergeInProgress || null;
  const missingReportStatus = merges.missingReportStatus || "idle";
  const missingReportError = merges.missingReportError || null;
  const missingReportSuccess = merges.missingReportSuccess || null;
  const deleteStatus = merges.deleteStatus || "idle";
  const deleteError = merges.deleteError || null;
  const deleteSuccess = merges.deleteSuccess || null;

  const handlePaymentStatusFilter = (newStatus: any) => {
    dispatch(changeFilter({ name: "invoicePaymentStatus", value: newStatus }));
  };

  const handleStatusToggle = (_event: any, newStatus: any) => {
    handlePaymentStatusFilter(newStatus);
  };

  // Debounced customer address filter: filters are applied server-side now,
  // so typing must not fire a request on every keystroke.
  const [addressInput, setAddressInput] = useState(
    filters.customerAddress || ""
  );

  // Keep the input in sync when the filter is cleared/reset externally.
  useEffect(() => {
    setAddressInput(filters.customerAddress || "");
  }, [filters.customerAddress]);

  useEffect(() => {
    if (addressInput === filters.customerAddress) return;
    const timer = setTimeout(() => {
      dispatch(changeFilter({ name: "customerAddress", value: addressInput }));
    }, 400);
    return () => clearTimeout(timer);
  }, [addressInput, filters.customerAddress, dispatch]);

  const handleCustomerAddressFilter = (event: any) => {
    setAddressInput(event.target.value);
  };

  // const handleIsPMHFilter = (event: any) => {
  //   dispatch(changeFilter({ name: "isPMH", value: event.target.checked }));
  // };

  // const handleIsOnlinePaymentFilter = (event: any) => {
  //   dispatch(
  //     changeFilter({ name: "isOnlinePayment", value: event.target.checked })
  //   );
  // };

  const handleEmailStatusToggle = (_event: any, newStatus: any) => {
    dispatch(changeFilter({ name: "emailSent", value: newStatus }));
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
  };

  const paymentStatusButtons = [
    { value: "0", label: "Paid in Full" },
    { value: "1", label: "Open" },
    { value: "2", label: "Partial Payment" },
    { value: "4", label: "Scheduled" },
    { value: "6", label: "In Process" },
  ];

  const emailStatusButtons = [
    { value: "true", label: "Sent" },
    { value: "false", label: "Not Sent" },
  ];

  const additionalFilters = [
    // <FormControlLabel
    //   key="isPMH"
    //   control={
    //     <Checkbox
    //       checked={filters.isPMH || false}
    //       onChange={handleIsPMHFilter}
    //       size="small"
    //       disabled={loading}
    //     />
    //   }
    //   label="PMH"
    //   sx={{
    //     "& .MuiFormControlLabel-label": {
    //       fontSize: "0.875rem",
    //     },
    //   }}
    // />,
    // <FormControlLabel
    //   key="isOnlinePayment"
    //   control={
    //     <Checkbox
    //       checked={filters.isOnlinePayment || false}
    //       onChange={handleIsOnlinePaymentFilter}
    //       size="small"
    //       disabled={loading}
    //     />
    //   }
    //   label="Online Payment"
    //   sx={{
    //     "& .MuiFormControlLabel-label": {
    //       fontSize: "0.875rem",
    //     },
    //   }}
    // />,
    <TextField
      key="customerAddress"
      size="small"
      placeholder="Customer Address"
      value={addressInput}
      onChange={handleCustomerAddressFilter}
      disabled={loading}
      sx={{
        minWidth: 200,
        "& .MuiInputBase-input": {
          padding: "4.5px 14px 4.5px 0",
        },
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          </InputAdornment>
        ),
        sx: { borderRadius: 0 },
      }}
    />,
  ];

  const buttonGroups = [
    {
      buttons: emailStatusButtons,
      filters: filters.emailSent || [],
      onChange: handleEmailStatusToggle,
      label: "email-status",
    },
  ];

  const actions = [
    ...(selectedVendors.length > 0
      ? [
          {
            label: `${selectedVendors.length} selected`,
            startIcon: <SendIcon />,
            loading: sendStatus === "loading",
            onClick: onSendLienWaiverNotices,
            disabled: sendStatus === "loading",
            children: "Send Lien Waiver Notices",
          },
        ]
      : []),
  ];

  const fullscreenButton = {
    startIcon: isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />,
    onClick: onToggleFullscreen,
    disabled: loading,
    children: isFullscreen ? "Exit Fullscreen" : "Fullscreen",
    variant: "outlined" as const,
    color: "secondary" as const,
  };

  const exportButton = {
    startIcon: <FileDownloadIcon />,
    onClick: onExport,
    disabled: loading,
    children: "Export to Excel",
    variant: "outlined" as const,
    color: "success" as const,
  };

  const syncButton = {
    startIcon: <SettingsIcon />,
    onClick: () => setSyncSettingsModalOpen(true),
    disabled: loading,
    children: "Sync Settings",
    variant: "contained" as const,
    color: "primary" as const,
  };

  const openInsuranceDialog = () => {
    if (!mergesFilters.year) {
      dispatch(
        setMergesFilter({ name: "year", value: String(currentYear - 1) })
      );
    }
    setInsuranceDialogOpen(true);
  };
  const closeInsuranceDialog = () => {
    setInsuranceDialogOpen(false);
    dispatch(clearGenerateSuccess());
    dispatch(clearMergeSuccess());
    dispatch(clearMergeInProgress());
    dispatch(clearMissingReportSuccess());
    dispatch(clearDeleteSuccess());
  };
  const initiateXlsxReport = () => dispatch(generateXlsxReport());
  const initiateFilesMerge = () => dispatch(initiateFilesMergeThunk());
  const getVendorsWithMissingFiles = () => dispatch(getMissingFilesReport());
  const handleDeleteMergeFile = (mergeId: any, fileName: any) => {
    setFileToDelete({ id: mergeId, name: fileName });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (fileToDelete) {
      dispatch(deleteVendorInsuranceMerge(fileToDelete.id));
    }
    setDeleteConfirmOpen(false);
    setFileToDelete(null);
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setFileToDelete(null);
  };

  const insuranceReportButton = {
    startIcon: <SettingsIcon />,
    onClick: openInsuranceDialog,
    disabled: loading,
    children: "Insurance Tools",
    variant: "outlined" as const,
    color: "info" as const,
  };

  useEffect(() => {
    if (insuranceDialogOpen) {
      dispatch(fetchVendorInsuranceMerges());
    }
  }, [
    insuranceDialogOpen,
    dispatch,
    mergesFilters.year,
    mergesFilters.from,
    mergesFilters.to,
    mergesFilters.createdBy,
    mergesPage,
    mergesLimit,
  ]);

  useEffect(() => {
    if (deleteSuccess) {
      enqueueSnackbar(deleteSuccess, { variant: "success" });
      dispatch(clearDeleteSuccess());
    }
  }, [deleteSuccess, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (deleteError) {
      enqueueSnackbar(deleteError, { variant: "error" });
      dispatch(clearDeleteSuccess());
    }
  }, [deleteError, enqueueSnackbar, dispatch]);

  // const tpFoldersReportButton = {
  //   startIcon: <FileDownloadIcon />,
  //   onClick: onDownloadTPFoldersReport,
  //   disabled: loading || tpFoldersReportStatus === "loading",
  //   loading: tpFoldersReportStatus === "loading",
  //   children: "TP Folders Report",
  //   variant: "outlined",
  //   color: "secondary",
  // };

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 3, // above the table's sticky header (zIndex 2) so the table
        // hides cleanly behind the bar when scrolling
        mb: 2,
        backgroundColor: "background.default",
      }}
    >
      <PaperWrapper>
        <TableControls
          buttons={paymentStatusButtons}
          filters={filters.invoicePaymentStatus || []}
          onChange={handleStatusToggle}
          onClear={handleClearFilters}
          additionalFilters={additionalFilters}
          buttonGroups={buttonGroups}
          syncButton={syncButton}
          fullscreenButton={fullscreenButton}
          exportButton={exportButton}
          insuranceReportButton={insuranceReportButton}
          // tpFoldersReportButton={tpFoldersReportButton}
          actions={actions}
          elevation={0}
          borderRadius="0"
          backgroundColor="background.paper"
          loading={loading}
        />
      </PaperWrapper>

      <Dialog
        open={insuranceDialogOpen}
        onClose={closeInsuranceDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 0,
          },
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
              Insurance Reports
            </Typography>
            <TextField
              select
              label="Year"
              size="small"
              value={mergesFilters.year || String(currentYear - 1)}
              onChange={(e) =>
                dispatch(
                  setMergesFilter({
                    name: "year",
                    value: String(e.target.value),
                  })
                )
              }
              sx={{
                minWidth: 100,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0,
                },
              }}
            >
              {yearOptions.map((y) => (
                <MenuItem key={y} value={String(y)}>
                  {y}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              onClick={initiateXlsxReport}
              disabled={generateStatus === "loading"}
              sx={{ borderRadius: 0, }}
            >
              {generateStatus === "loading" ? (
                <CircularProgress size={24} sx={{ mr: 1 }} />
              ) : null}
              Generate XLSX
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={initiateFilesMerge}
              disabled={mergeStatus === "loading"}
              sx={{ borderRadius: 0, }}
            >
              {mergeStatus === "loading" ? (
                <CircularProgress size={24} sx={{ mr: 1 }} />
              ) : null}
              Merge Files
            </Button>
            <Button
              variant="outlined"
              onClick={getVendorsWithMissingFiles}
              disabled={missingReportStatus === "loading"}
              sx={{ borderRadius: 0, }}
            >
              {missingReportStatus === "loading" ? (
                <CircularProgress size={24} sx={{ mr: 1 }} />
              ) : null}
              Missing Files Report
            </Button>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {generateSuccess ? (
            <Alert
              severity="success"
              sx={{ mb: 2, borderRadius: 0 }}
              onClose={() => dispatch(clearGenerateSuccess())}
            >
              {generateSuccess}
            </Alert>
          ) : null}

          {generateError ? (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 0 }}
              onClose={() => dispatch(clearGenerateSuccess())}
            >
              {generateError}
            </Alert>
          ) : null}

          {mergeSuccess ? (
            <Alert
              severity="success"
              sx={{ mb: 2, borderRadius: 0 }}
              onClose={() => dispatch(clearMergeSuccess())}
            >
              {mergeSuccess}
            </Alert>
          ) : null}

          {mergeInProgress ? (
            <Alert
              severity="warning"
              sx={{ mb: 2, borderRadius: 0 }}
              onClose={() => dispatch(clearMergeInProgress())}
            >
              <Box
                component="pre"
                sx={{
                  fontFamily: "inherit",
                  fontSize: "0.875rem",
                  margin: 0,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {mergeInProgress}
              </Box>
            </Alert>
          ) : null}

          {mergeError ? (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 0 }}
              onClose={() => dispatch(clearMergeSuccess())}
            >
              {mergeError}
            </Alert>
          ) : null}

          {missingReportSuccess ? (
            <Alert
              severity="success"
              sx={{ mb: 2, borderRadius: 0 }}
              onClose={() => dispatch(clearMissingReportSuccess())}
            >
              {missingReportSuccess}
            </Alert>
          ) : null}

          {missingReportError ? (
            <Alert
              severity="error"
              sx={{ mb: 2, borderRadius: 0 }}
              onClose={() => dispatch(clearMissingReportSuccess())}
            >
              {missingReportError}
            </Alert>
          ) : null}

          <Typography
            variant="subtitle1"
            sx={{ mb: 1.5, mt: 2, fontWeight: 600 }}
          >
            Merged Files
          </Typography>

          {mergedListStatus === "loading" ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress />
            </Box>
          ) : mergedListError ? (
            <Typography variant="body2" color="error">
              {mergedListError}
            </Typography>
          ) : mergedFiles.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No merged files yet.
            </Typography>
          ) : (
            <List>
              {mergedFiles.map((f: any) => (
                <ListItem key={f.id} divider>
                  <ListItemText
                    primary={f.fileName || f.name}
                    secondary={f.createdBy || null}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (f.sUrl) {
                        const a = document.createElement("a");
                        a.href = f.sUrl;
                        a.download = f.fileName || `merge_${f.id}.pdf`;
                        a.target = "_blank";
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                      }
                    }}
                    disabled={deleteStatus === "loading"}
                    sx={{ mr: 0.5 }}
                  >
                    <FileDownloadIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() =>
                      handleDeleteMergeFile(f.id, f.fileName || f.name)
                    }
                    disabled={deleteStatus === "loading"}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ gap: 1, alignItems: "center", px: 3, pb: 2 }}>
          <Button
            onClick={() => dispatch(setMergesPage(Math.max(1, mergesPage - 1)))}
            disabled={mergedListStatus === "loading" || mergesPage <= 1}
            sx={{ borderRadius: 0, }}
          >
            Prev
          </Button>
          <Typography sx={{ mx: 1, fontSize: "0.875rem" }}>
            Page {mergesPage}
            {mergesTotalPages ? ` of ${mergesTotalPages}` : ""}
          </Typography>
          <Button
            onClick={() => dispatch(setMergesPage(mergesPage + 1))}
            disabled={
              mergedListStatus === "loading" ||
              !mergesTotalPages ||
              mergesPage >= mergesTotalPages
            }
            sx={{ borderRadius: 0, }}
          >
            Next
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={closeInsuranceDialog}
            sx={{ borderRadius: 0, }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <SyncSettingsModal
        open={syncSettingsModalOpen}
        onClose={() => setSyncSettingsModalOpen(false)}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={cancelDelete}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 0,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{fileToDelete?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={cancelDelete}
            sx={{ borderRadius: 0, }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            color="error"
            variant="contained"
            disabled={deleteStatus === "loading"}
            sx={{ borderRadius: 0, }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorsTableControls;
